import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, ShieldAlert } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Field, Input, Textarea } from "@/components/kit";
import { AddressLookup } from "@/components/AddressLookup";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext, createBooking } from "@/lib/driverData";
import { api, fileToPhotoPayload } from "@/lib/api";
import { useDraftPersistence, clearDraft } from "@/lib/useDraftPersistence";

export default function DriverBodywork() {
  const nav = useNavigate();
  const { data: ctx, loading } = useAsync(() => loadDriverContext(), []);
  const [form, setForm] = useState({
    incident_date: "", safe_to_drive: "yes", drivable: "yes",
    third_party: "no", third_party_details: "", injuries: "no",
    description: "", address: {},
  });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const draftScope = ctx?.vehicle?.id ? `bodywork_${ctx.vehicle.id}` : null;
  useDraftPersistence({ scope: draftScope, form, setForm, enabled: !!draftScope, onRestore: () => toast.info("Restored your previous progress") });

  if (loading) return <DriverShell back="/driver"><Spinner /></DriverShell>;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const details = {
        flow: "accident_bodywork",
        incident_date: form.incident_date || null,
        drivable: form.drivable === "yes",
        third_party_involved: form.third_party === "yes",
        third_party_details: form.third_party === "yes" ? (form.third_party_details || null) : null,
        injuries: form.injuries === "yes",
      };
      if (form.address?.address_text) details.location = form.address;
      const id = await createBooking(ctx, {
        request_type: "accident", request_category: "accident_bodywork", request_details: details,
        safe_to_drive: form.safe_to_drive === "yes",
        recovery_required: form.drivable === "no",
        description: form.description || null,
        location_text: form.address?.address_text || null, postcode: form.address?.postcode || null,
        priority: (form.safe_to_drive === "no" || form.injuries === "yes") ? "high" : "normal",
      });
      if (files.length) {
        try {
          const payloads = await Promise.all(files.map(fileToPhotoPayload));
          const res = await api.driverUploadBookingPhotos(id, payloads);
          if (res.uploaded < files.length) toast.warning("Report saved; some photos could not be uploaded.");
        } catch (_) { toast.warning("Report saved, but the photos could not be uploaded."); }
      }
      clearDraft(draftScope);
      toast.success("Report submitted and routed to your Venson team.");
      nav(`/driver/booking/${id}`, { replace: true });
    } catch (err) {
      toast.error(err.message || "We couldn't submit your report. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <DriverShell back="/driver" title="Accident & bodywork">
      <Panel className="p-4 mb-4 bg-[hsl(var(--vda-red))]/8 border-[hsl(var(--vda-red))]/30 flex gap-2" data-testid="accident-notice">
        <ShieldAlert className="h-5 w-5 text-[hsl(var(--vda-red))] flex-none" />
        <p className="text-sm text-foreground/80">
          If anyone is hurt or in danger, call <strong>999</strong> first. This report logs the incident for your Venson team — it does not summon emergency services.
        </p>
      </Panel>
      <form onSubmit={submit} className="space-y-4" data-testid="bodywork-form">
        <Field label="When did it happen?">
          <Input data-testid="body-date" type="date" value={form.incident_date} onChange={(e) => set("incident_date", e.target.value)} />
        </Field>
        <Field label="Is the vehicle safe to drive?">
          <div className="grid grid-cols-2 gap-3">
            {["yes", "no"].map((v) => (
              <button key={v} type="button" data-testid={`body-safe-${v}`} onClick={() => set("safe_to_drive", v)}
                className={`h-11 rounded-[6px] border text-sm font-medium ${form.safe_to_drive === v ? (v === "no" ? "border-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red))]/10 text-[hsl(var(--vda-red))]" : "border-primary bg-primary/10 text-primary") : "border-[hsl(var(--input))]"}`}>
                {v === "yes" ? "Yes, safe" : "No, not safe"}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Can the vehicle be driven, or does it need recovery?">
          <div className="grid grid-cols-2 gap-3">
            {[["yes", "Drivable"], ["no", "Needs recovery"]].map(([v, label]) => (
              <button key={v} type="button" data-testid={`body-drivable-${v}`} onClick={() => set("drivable", v)}
                className={`h-11 rounded-[6px] border text-sm font-medium ${form.drivable === v ? "border-primary bg-primary/10 text-primary" : "border-[hsl(var(--input))]"}`}>
                {label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Was another party involved?">
          <div className="grid grid-cols-2 gap-3">
            {[["no", "No"], ["yes", "Yes"]].map(([v, label]) => (
              <button key={v} type="button" data-testid={`body-thirdparty-${v}`} onClick={() => set("third_party", v)}
                className={`h-11 rounded-[6px] border text-sm font-medium ${form.third_party === v ? "border-primary bg-primary/10 text-primary" : "border-[hsl(var(--input))]"}`}>
                {label}
              </button>
            ))}
          </div>
        </Field>
        {form.third_party === "yes" && (
          <Field label="Other party details">
            <Textarea data-testid="body-thirdparty-details" rows={3} value={form.third_party_details} onChange={(e) => set("third_party_details", e.target.value)} placeholder="Name, vehicle registration, insurer if known" />
          </Field>
        )}
        <Field label="Was anyone injured?">
          <div className="grid grid-cols-2 gap-3">
            {[["no", "No"], ["yes", "Yes"]].map(([v, label]) => (
              <button key={v} type="button" data-testid={`body-injuries-${v}`} onClick={() => set("injuries", v)}
                className={`h-11 rounded-[6px] border text-sm font-medium ${form.injuries === v ? (v === "yes" ? "border-[hsl(var(--vda-red))] bg-[hsl(var(--vda-red))]/10 text-[hsl(var(--vda-red))]" : "border-primary bg-primary/10 text-primary") : "border-[hsl(var(--input))]"}`}>
                {label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="What happened?">
          <Textarea data-testid="body-description" rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the damage and how it happened" />
        </Field>
        <Field label="Photographs (optional)">
          {files.length > 0 && (
            <ul className="space-y-1 mb-2" data-testid="body-photo-list">
              {files.map((fl, i) => (
                <li key={i} className="vda-panel p-2.5 flex items-center justify-between">
                  <span className="text-sm truncate">{fl.name}</span>
                  <button type="button" data-testid={`body-photo-remove-${i}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
          <label className="vda-panel p-4 flex items-center gap-2 cursor-pointer text-sm text-muted-foreground">
            <Upload className="h-4 w-4" /> {files.length ? "Add more photos" : "Add photos of the damage"}
            <input data-testid="body-photo" type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])].slice(0, 10))} />
          </label>
        </Field>
        <AddressLookup label="Where is the vehicle now?" value={form.address} onChange={(a) => set("address", a)} testIdPrefix="body-address" />
        <Btn type="submit" loading={saving} data-testid="bodywork-submit" className="w-full">Submit report</Btn>
      </form>
    </DriverShell>
  );
}
